import { Badge as CompassBadge } from "@circleco/compass/components/Badge";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";
import type { ComponentPropsWithRef } from "react";

import type { StatusBadgeProps as UIKitStatusBadgeProps } from "@/react/components/shared/uikit/StatusBadge";
import { StatusBadge as UIKitStatusBadge } from "@/react/components/shared/uikit/StatusBadge";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

// eslint-disable-next-line no-barrel-files/no-barrel-files -- This is a compatibility layer that re-exports types for SettingsApp
export type { StatusBadgeProps } from "@/react/components/shared/uikit/StatusBadge";

type CompassBadgeProps = ComponentPropsWithRef<typeof CompassBadge>;

const mapVariant = (
  variant: UIKitStatusBadgeProps["variant"] | undefined,
): CompassBadgeProps["variant"] => {
  switch (variant) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "danger":
      return "destructive";
    case "info":
      return "info";
    case "default":
    default:
      return "secondary";
  }
};

export const StatusBadge = (props: UIKitStatusBadgeProps) => {
  const isCircleV4 = useIsCircleV4FeatureEnabled("circle_v4_badges_enabled");

  if (isCircleV4) {
    const { variant, textTransform, children, ...restProps } = props;

    // Fall back to UIKit if children is not a string or number
    if (typeof children !== "string" && typeof children !== "number") {
      return <UIKitStatusBadge {...props} />;
    }

    const compassVariant = mapVariant(variant);

    const compassProps: CompassBadgeProps = {
      ...restProps,
      variant: compassVariant,
      label: String(children),
      textClassName: textTransform
        ? allowedClassesFor("Badge", textTransform)
        : "",
    };

    return <CompassBadge {...compassProps} data-testid="status-badge" />;
  }

  return <UIKitStatusBadge {...props} />;
};
